import type { FormValues } from '~/shared/forms/types';
import { satisifiesWhenConditions } from './when';
import { FormControlConstants, type FormControlModel, type FormControlOptionModel, type FormFieldModel } from '@generated';

export function hideControlsAndOptions(formControls: FormControlModel[], formFields: FormFieldModel[], formValues: FormValues): FormControlModel[] {
	return markControlsReadOnly(
		formControls.map(control => {
			if (control.hideWhen && control.hideWhen.length > 0 && satisifiesWhenConditions(control.hideWhen, formValues)) {
				return { ...control, hidden: true, formControls: [], options: [] };
			}
			if (control.controlType === FormControlConstants.formControlTypes.bodyStructureControlArray) {
				return control;
			}
			return {
				...control,
				hidden: control.hidden ?? false,
				formControls: control.formControls ? hideControlsAndOptions(control.formControls, formFields, formValues) : [],
				options: control.options ? hideControlOptions(control, control.options, formFields, formValues) : []
			};
		}),
		formValues
	);
}

function markControlsReadOnly(formControls: FormControlModel[], formValues: FormValues): FormControlModel[] {
	return formControls.map((control): FormControlModel => {
		if (control.hidden) {
			return control;
		}

		if (control.readOnlyWhen && control.readOnlyWhen.length > 0 && satisifiesWhenConditions(control.readOnlyWhen, formValues)) {
			return { ...control, isReadOnly: true };
		}
		return {
			...control,
			formControls: control.formControls ? markControlsReadOnly(control.formControls, formValues) : [],
			options: control.options?.map(option => {
				if (!option.formControls) {
					return option;
				}
				return {
					...option,
					formControls: markControlsReadOnly(option.formControls, formValues)
				};
			})
		};
	});
}

function hideControlOptions(
	formControl: FormControlModel,
	options: FormControlOptionModel[],
	formFields: FormFieldModel[],
	formValues: FormValues
): FormControlOptionModel[] {
	const formField = formFields.find(field => field.name === formControl.name);
	if (!formField) {
		throw new Error(`Form field not found for control name: ${formControl.name}`);
	}

	const mustBeOnlySelectedValueRule = formField.validationRules?.find(
		rule => rule.rule === FormControlConstants.validationRules.mustBeOnlySelectedValue
	);

	if (mustBeOnlySelectedValueRule && formValues[formField.name]?.includes(mustBeOnlySelectedValueRule.value)) {
		if (formControl.hideMustBeOnlySelected !== false) {
			return options
				.filter(option => option.value === mustBeOnlySelectedValueRule.value)
				.map(option => {
					return {
						...option,
						formControls: option.formControls ? hideControlsAndOptions(option.formControls, formFields, formValues) : []
					};
				});
		}
		return options.map(option => {
			return {
				...option,
				disabled: option.value !== mustBeOnlySelectedValueRule.value,
				formControls: option.formControls ? hideControlsAndOptions(option.formControls, formFields, formValues) : []
			};
		});
	}
	return options.map((option: FormControlOptionModel) => {
		if (option.hideWhen && option.hideWhen.length > 0 && satisifiesWhenConditions(option.hideWhen, formValues)) {
			return {
				...option,
				formControls: [],
				hidden: true
			};
		}
		return {
			...option,
			formControls: option.formControls ? hideControlsAndOptions(option.formControls, formFields, formValues) : []
		};
	});
}
